import { useState, useEffect } from "react";
import {
  Box, Container, Typography,
  Card, CardContent, Chip, Stack, Divider, CircularProgress,
} from "@mui/material";
import {
  Gavel as LegalIcon,
  VerifiedUser as QAIcon,
  Business as CEOIcon,
  FormatQuote as QuoteIcon,
  Groups as DefaultIcon,
} from "@mui/icons-material";

const API_BASE = "https://www.africabuildtoshelter.com/api";
const IMG_BASE = "https://www.africabuildtoshelter.com";

const THEME = {
  brown:     "#5C3311",
  brownDark: "#3e220b",
  gold:      "#DDA63A",
  cream:     "#FDF8F2",
};

const SDG_COLORS = [
  "#E5243B","#DDA63A","#4C9F38","#C5192D","#FF3A21",
  "#26BDE2","#FCC30B","#A21942","#FD6925","#DD1367",
  "#FD9D24","#BF8B2E","#3F7E44","#0A97D9","#56C02B",
  "#00689D","#19486A",
];

const DEPT_STYLES = {
  Executive: {
    icon:        <CEOIcon sx={{ fontSize: 22 }} />,
    color:       { bg: "#19486A", light: "#e8f0f7" },
    description: `BTSA's executive leadership drives the strategic vision across 54 African nations, forging partnerships, mobilising resources, and championing the right to adequate shelter as a fundamental human right.`,
  },
  Legal: {
    icon:        <LegalIcon sx={{ fontSize: 22 }} />,
    color:       { bg: THEME.brownDark, light: "#f5ece3" },
    description: `The legal team at BTSA plays a vital role in ensuring that the organisation operates with integrity, compliance, and accountability. Our team provides legal advisory services, supports contract drafting and review, and ensures adherence to national and international regulations.`,
  },
  "Quality Assurance": {
    icon:        <QAIcon sx={{ fontSize: 22 }} />,
    color:       { bg: "#3F7E44", light: "#eaf4eb" },
    description: `The QA team ensures every BTSA project meets the highest standards of quality and delivery. From field inspections to process audits, we guarantee that every shelter built reflects our commitment to excellence and community impact.`,
  },
  _default: {
    icon:        <DefaultIcon sx={{ fontSize: 22 }} />,
    color:       { bg: THEME.brown, light: "#f5ece3" },
    description: "",
  },
};

const FALLBACK_DEPARTMENTS = [
  {
    id: 1, name: "Executive",
    members: [
      { id: 1, name: "Lewis Bisaso", role: "Chief Executive Officer", photo: "/CEO.jpeg",
        quote: "Building Africa one shelter at a time — leadership with purpose.", department: "Executive" },
    ],
  },
  {
    id: 2, name: "Legal",
    members: [
      { id: 2, name: "Namanda Patience", role: "Senior Legal Advisor", photo: "/namanda-patience.jpeg",
        quote: "Integrity and compliance are the foundation of every lasting institution.", department: "Legal" },
      { id: 3, name: "Kasirye Mahad", role: "Assistant Legal Advisor", photo: "/kasirye-mahad.jpeg",
        quote: "Upholding ethics and accountability in every contract we sign.", department: "Legal" },
    ],
  },
  {
    id: 3, name: "Quality Assurance",
    members: [
      { id: 4, name: "Miss Lenganji", role: "Quality Assurance Manager", photo: "/lenganji.jpeg",
        quote: "Excellence isn't optional — it's the standard we set every day.", department: "Quality Assurance" },
    ],
  },
];

const CARD_IMG_H = 400;
const CARD_W = 360;

function useApi(endpoint) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API_BASE}${endpoint}`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [endpoint]);
  return { data, loading, error };
}

function getImageUrl(path) {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${IMG_BASE}${path.startsWith("/") ? path : `/${path}`}`;
}

function getDeptStyle(name) {
  return DEPT_STYLES[name] ?? DEPT_STYLES._default;
}

const sdgConic = () =>
  SDG_COLORS.map((c, i) =>
    `${c} ${(i / SDG_COLORS.length) * 100}% ${((i + 1) / SDG_COLORS.length) * 100}%`
  ).join(", ");

function SdgStrip({ height = "5px" }) {
  return (
    <Box sx={{ display: "flex", height }}>
      {SDG_COLORS.map((c, i) => <Box key={i} sx={{ flex: 1, bgcolor: c }} />)}
    </Box>
  );
}

function MemberCard({ member, index }) {
  const deptName = member.department_name ?? member.department ?? "";
  const { icon, color } = getDeptStyle(deptName);
  const photo = getImageUrl(member.photo_url ?? member.photo);
  const [imgError, setImgError] = useState(false);
  const showPhoto = photo && !imgError;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: "18px",
        overflow: "hidden",
        border: "1px solid rgba(0,0,0,0.08)",
        bgcolor: "#fff",
        display: "flex",
        flexDirection: "column",
        width: CARD_W,
        flexShrink: 0,
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        "&:hover": { transform: "translateY(-6px)", boxShadow: "0 20px 48px rgba(92,51,17,0.15)" },
        animation: `fadeUp 0.5s ease ${index * 0.12}s both`,
        "@keyframes fadeUp": {
          from: { opacity: 0, transform: "translateY(28px)" },
          to:   { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <SdgStrip height="4px" />

      {/* Fixed image area — identical height whether photo exists or not */}
      <Box sx={{
        position: "relative",
        width: "100%",
        height: CARD_IMG_H,
        flexShrink: 0,
        overflow: "hidden",
      }}>
        {showPhoto ? (
          <Box
            component="img"
            src={photo}
            alt={member.name}
            onError={() => setImgError(true)}
            sx={{
              width: "100%", height: "100%",
              objectFit: "cover", objectPosition: "top center",
              display: "block",
              transition: "transform 0.45s ease",
              ".MuiCard-root:hover &": { transform: "scale(1.04)" },
            }}
          />
        ) : (
          <Box sx={{
            width: "100%", height: "100%",
            bgcolor: color.light,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Box sx={{
              width: 110, height: 110, borderRadius: "50%",
              bgcolor: color.bg,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontSize: 48,
              fontFamily: "'Playfair Display', serif", fontWeight: 700,
            }}>
              {(member.name ?? "?")[0].toUpperCase()}
            </Box>
          </Box>
        )}

        {/* Department chip — bigger and more readable */}
        <Chip
          label={deptName}
          size="small"
          sx={{
            position: "absolute", top: 14, right: 14,
            bgcolor: "rgba(255,255,255,0.95)", backdropFilter: "blur(8px)",
            fontWeight: 700,
            fontSize: "13px",        // was 10px — now properly readable
            letterSpacing: "0.3px",
            color: color.bg,
            border: `1px solid ${color.bg}40`,
            textTransform: "uppercase",
            height: 32,
            px: 1,
          }}
        />
      </Box>

      <CardContent sx={{ p: 3, pt: 3, flexGrow: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {/* Member name */}
        <Typography sx={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "22px",          // was 19px
          fontWeight: 700,
          color: "#1a1a1a",
          lineHeight: 1.25,
        }}>
          {member.name}
        </Typography>

        {/* Role */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "center",
            width: 32, height: 32, borderRadius: "50%",
            bgcolor: color.light, color: color.bg,
            flexShrink: 0,
          }}>
            {icon}
          </Box>
          <Typography sx={{
            fontSize: "14px",          // was 11px — doubled in readability
            fontWeight: 700,
            color: color.bg,
            letterSpacing: "0.3px",
            textTransform: "uppercase",
            lineHeight: 1.3,
          }}>
            {member.role}
          </Typography>
        </Stack>

        <Divider sx={{ borderColor: "rgba(0,0,0,0.08)" }} />

        {/* Quote */}
        {member.quote && (
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
            <QuoteIcon sx={{ color: THEME.gold, fontSize: 24, flexShrink: 0, mt: 0.3 }} />
            <Typography sx={{
              fontSize: "15px",          // was 13px
              color: "#555",
              fontStyle: "italic",
              lineHeight: 1.7,
            }}>
              {member.quote}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function DepartmentSection({ dept }) {
  const { icon, color, description } = getDeptStyle(dept.name);
  const members = dept.members ?? [];
  const deptDesc = dept.description || description;

  return (
    <Box mb={10}>
      {/* Centred department header */}
      <Box sx={{ textAlign: "center", mb: 5 }}>
        <Box sx={{
          width: 60, height: 60, borderRadius: "16px",
          bgcolor: color.light,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          color: color.bg, mb: 2,
        }}>
          {icon}
        </Box>

        {/* Department name */}
        <Typography sx={{
          fontFamily: "'Playfair Display', serif",
          fontSize: { xs: "26px", md: "34px" },  // was 22/30px
          fontWeight: 700,
          color: THEME.brownDark,
          lineHeight: 1.1,
        }}>
          {dept.name}
        </Typography>

        {/* Member count label */}
        <Typography sx={{
          fontSize: "13px",                        // was 9px — nearly invisible
          letterSpacing: "2px",
          textTransform: "uppercase",
          color: THEME.gold,
          fontWeight: 700,
          mt: 0.8,
        }}>
          BTSA · {members.length} member{members.length !== 1 ? "s" : ""}
        </Typography>

        {/* Department description */}
        {deptDesc && (
          <Typography sx={{
            fontSize: "16px",                      // was 14px
            color: "#666",
            lineHeight: 1.85,
            maxWidth: 620,
            mx: "auto",
            mt: 2.5,
          }}>
            {deptDesc}
          </Typography>
        )}
      </Box>

      {/* Cards row — fixed width per card, centred */}
      <Box sx={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: 4,
      }}>
        {members.map((member, i) => (
          <MemberCard key={member.id ?? member.name} member={member} index={i} />
        ))}
      </Box>
    </Box>
  );
}

function HeroBanner({ totalMembers, totalDepts }) {
  return (
    <Box sx={{
      background: `linear-gradient(135deg, ${THEME.brownDark} 0%, ${THEME.brown} 60%, #8B5A2B 100%)`,
      py: { xs: 8, md: 12 },
      px: { xs: 2, md: 4 },
      position: "relative",
      overflow: "hidden",
      textAlign: "center",
    }}>
      {[
        { top: -60,    right: -60, size: 320, opacity: 0.12 },
        { bottom: -80, left:  -40, size: 240, opacity: 0.08 },
      ].map((s, i) => (
        <Box key={i} sx={{
          position: "absolute", width: s.size, height: s.size,
          borderRadius: "50%",
          background: `conic-gradient(${sdgConic()})`,
          opacity: s.opacity,
          ...(s.top    !== undefined && { top:    s.top }),
          ...(s.bottom !== undefined && { bottom: s.bottom }),
          ...(s.right  !== undefined && { right:  s.right }),
          ...(s.left   !== undefined && { left:   s.left }),
        }} />
      ))}

      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
        {/* Breadcrumb */}
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" mb={3}>
          {["About", "Our Team"].map((crumb, i, arr) => (
            <Stack key={crumb} direction="row" spacing={1} alignItems="center">
              <Typography sx={{
                fontSize: "14px",          // was 12px
                color: i === arr.length - 1 ? THEME.gold : "rgba(255,255,255,0.60)",
                fontWeight: i === arr.length - 1 ? 700 : 400,
              }}>
                {crumb}
              </Typography>
              {i < arr.length - 1 && (
                <Typography sx={{ fontSize: "16px", color: THEME.gold }}>›</Typography>
              )}
            </Stack>
          ))}
        </Stack>

        {/* Main heading */}
        <Typography sx={{
          fontFamily: "'Playfair Display', serif",
          fontSize: { xs: "36px", sm: "48px", md: "60px" },
          fontWeight: 700,
          color: "#fff",
          lineHeight: 1.12,
          mb: 2.5,
        }}>
          Meet the People<br />Behind BTSA
        </Typography>

        {/* Subtitle */}
        <Typography sx={{
          fontSize: { xs: "16px", md: "18px" },  // was 14/16px
          color: "rgba(255,255,255,0.78)",
          maxWidth: 520,
          mx: "auto",
          lineHeight: 1.8,
          mb: 5,
        }}>
          A dedicated team of professionals united by a single mission — building dignified,
          sustainable shelter for every African family.
        </Typography>

        {/* Stats */}
        <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" gap={2}>
          {[
            { label: "Team Members", value: totalMembers },
            { label: "Departments",  value: totalDepts },
            { label: "Countries",    value: "54" },
          ].map((stat) => (
            <Box key={stat.label} sx={{
              px: 3.5, py: 2,
              borderRadius: "30px",
              bgcolor: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)",
              border: "1px solid rgba(255,255,255,0.18)",
              display: "flex", alignItems: "center", gap: 1.5,
            }}>
              <Typography sx={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "30px",      // was 24px
                fontWeight: 700,
                color: THEME.gold,
                lineHeight: 1,
              }}>
                {stat.value}
              </Typography>
              <Typography sx={{
                fontSize: "14px",      // was 12px
                color: "rgba(255,255,255,0.70)",
                fontWeight: 600,
              }}>
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Container>
    </Box>
  );
}

export default function TeamPage() {
  const { data, loading } = useApi("/departments/");

  const departments = (() => {
    if (data) {
      const raw = Array.isArray(data) ? data : (data.results ?? []);
      return raw.map((dept) => ({
        ...dept,
        members: (dept.members ?? []).map((m) => ({
          ...m,
          department_name: dept.name,
        })),
      }));
    }
    return FALLBACK_DEPARTMENTS;
  })();

  const totalMembers = departments.reduce((sum, d) => sum + (d.members?.length ?? 0), 0);
  const totalDepts   = departments.length;

  return (
    <Box sx={{ bgcolor: THEME.cream, minHeight: "100vh", pt: { xs: 10, md: 12 } }}>
      <HeroBanner totalMembers={totalMembers} totalDepts={totalDepts} />
      <SdgStrip />
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress sx={{ color: THEME.brown }} />
          </Box>
        ) : (
          departments.map((dept) => (
            <DepartmentSection key={dept.id ?? dept.name} dept={dept} />
          ))
        )}
      </Container>
    </Box>
  );
}